import { parseHtmlString } from "@/utils/helpers/html";
import { Heading, Paragraph } from "@/components/ui/Typography"
import { WebsitePageSection } from "@/types"
import EditableSection from "@/components/admin/Content/WebPages/EditableSection";
import InlineVideoHighlightEditor from "@/components/admin/Content/WebPages/InlineEditors/InlineVideoHighlightEditor";
import { useHomePageHighlights } from "@/lib/api/hooks/video";

// Helper function to format date
const formatPublishedDate = (dateString: string | null): string => {
    if (!dateString) return '';
    try {
        const date = new Date(dateString);
        return date.toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' });
    } catch {
        return '';
    }
};

const VideoHighlight = ({ data }: { data: WebsitePageSection }) => {
    // Fetch live video highlights
    const { data: highlightsData } = useHomePageHighlights();
    const videoHighlight = highlightsData?.data?.[0];

    return (
        <EditableSection
            sectionTag={data.sectionTag}
            contentType={data.contentType}
            data={data}
            customEditor={InlineVideoHighlightEditor}
        >
            <div className="flex flex-col gap-4">
                {/* Header */}
                <div className="flex flex-col gap-2">
                    <Paragraph className="bg-gray-50 rounded-2xl px-3 py-1 !text-sm flex items-center justify-center text-forestGreen-100 w-fit">
                        Video Highlight (1/1)
                    </Paragraph>
                    <div className="flex items-center gap-6">
                        <Heading level={4}>{data.sectionTitle ?? "Video Highlight"}</Heading>
                    </div>
                    <Paragraph dangerouslySetInnerHTML={parseHtmlString(data.description ?? "Featured video highlight for the home page")} />
                </div>

                {/* Video Preview */}
                {videoHighlight ? (
                    <div className="flex items-start gap-4 p-4 bg-gray-50 rounded-lg border border-gray-200">
                        <div className="relative w-32 h-20 bg-gray-200 rounded overflow-hidden flex-shrink-0 flex items-center justify-center">
                            <svg className="w-10 h-10 text-gray-400" fill="currentColor" viewBox="0 0 20 20">
                                <path d="M10 18a8 8 0 100-16 8 8 0 000 16zM9.555 7.168A1 1 0 008 8v4a1 1 0 001.555.832l3-2a1 1 0 000-1.664l-3-2z" />
                            </svg>
                        </div>
                        <div className="flex-1 min-w-0">
                            <h5 className="font-semibold text-sm text-gray-900 truncate">{videoHighlight.title}</h5>
                            {videoHighlight.datePublished && (
                                <p className="text-xs text-gray-500 mt-1">
                                    <span className="font-medium">Published date:</span> {formatPublishedDate(videoHighlight.datePublished)}
                                </p>
                            )}
                            {videoHighlight.embeddedLink && (
                                <p className="text-xs text-forestGreen-100 mt-1 truncate">{videoHighlight.embeddedLink}</p>
                            )}
                        </div>
                    </div>
                ) : (
                    <div className="p-6 text-center bg-gray-50 rounded-lg border border-gray-200">
                        <p className="text-gray-500 text-sm">No video selected. Click to select one.</p>
                    </div>
                )}
            </div>
        </EditableSection>
    )
}

export default VideoHighlight
